import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { CommitOnEnterDirective } from 'src/app/shared/directives/commit-on-enter.directive';
import { SelectOnFocusDirective } from 'src/app/shared/directives/select-on-focus.directive';
import { TwoDecimalsDirective } from 'src/app/shared/directives/two-decimals.directive';
import { ReallocationV2Store } from '../../state/reallocation-v2.store';

/** Step 4 — set aside cash; shows the resulting investable amount. */
@Component({
  selector: 'app-rav2-protected-cash-step',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, SelectOnFocusDirective, CommitOnEnterDirective,
    TwoDecimalsDirective,
  ],
  templateUrl: './protected-cash-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProtectedCashStepComponent implements AfterViewInit {
  readonly store = inject(ReallocationV2Store);
  private readonly cashInput = viewChild<ElementRef<HTMLInputElement>>('cashInput');

  ngAfterViewInit(): void {
    // Focus (and select, via appSelectOnFocus) the cash input on entering this step.
    this.cashInput()?.nativeElement.focus();
  }

  /**
   * Whether the sweep control should be offered.
   *
   * Two directions, and they are not symmetrical. Money still to place can always be set aside.
   * An over-commitment can only be relieved from here if there IS a set-aside to give back —
   * otherwise the excess belongs to the models or the buys, and a button that could not change
   * anything is worse than no button.
   */
  canApplyRemaining(): boolean {
    const remaining = this.store.unappliedValue();
    if (remaining >= 0.005) return true;
    return remaining <= -0.005 && this.store.cashInput() > 0;
  }

  /** True when the outstanding amount is an over-commitment rather than money still to place. */
  isOverAllocated(): boolean {
    return this.store.unappliedValue() < 0;
  }

  /**
   * What the button says.
   *
   * ⚠️ NO AMOUNT, and the same wording as the Allocate to Models control -- both offer the same
   * gesture on the same figure, so reading them as two different operations was the confusion.
   * It said `Set aside remaining $3,207.50`. The figure moved to the info icon beside it, where
   * it can be read on demand without the button re-flowing as the remainder changes.
   *
   * Still names the DIRECTION, because the same control both sets money aside and hands it back.
   */
  remainingLabel(): string {
    return this.store.unappliedValue() > 0 ? 'Allocate Remaining' : 'Return to Invest';
  }

  /**
   * The amount actually in play, which is NOT simply the outstanding figure.
   *
   * ⚠️ The give-back is capped at what has been set aside, so the sentence cannot promise more
   * than the button can deliver -- {@link ReallocationV2Store.applyRemainingToCash} clamps at
   * zero for the same reason. The set-aside direction has no such cap: anything unplaced can be
   * held back.
   */
  private movable(): number {
    const remaining = this.store.unappliedValue();
    return remaining > 0 ? remaining : Math.min(this.store.cashInput(), -remaining);
  }

  /**
   * The sentence behind the info icon, and the button's own accessible name.
   *
   * ⚠️ Names SET-ASIDE CASH as the destination, where the Allocate to Models control names the
   * total model amount. The two buttons deliberately read alike; the sentence is what says which
   * pot the money is going into, so it is the one part that must not be copied verbatim.
   */
  remainingHint(): string {
    const money = this.movable().toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return this.store.unappliedValue() > 0
      // ⚠️ "Set-aside cash" is the destination now. Renamed everywhere on 2026-09-15; these two
      // reach the advisor only through a tooltip and an aria-label, which is how they survived.
      ? `Allocate remaining ${money} to set-aside cash.`
      : `Return ${money} from set-aside cash to the investable amount.`;
  }
}
